import PlanoAcao from '../types/negocio/PlanoAcao';
import PlanoAcaoResultadoConsulta from '../types/negocio/PlanoAcaoResultadoConsulta';
import AcaoPorArea from '../types/negocio/AcaoPorArea';
import api from './api';
import BaseService from './BaseService';

class PlanoAcaoService extends BaseService<PlanoAcao> {
  constructor() {
    super('plano-acao', api);
  }

  async buscarPlanos(filtros?: {
    estrategia?: string;
    responsavel?: string;
    prazo?: string;
    area?: string;
    status?: string;
    termo?: string;
    verArquivados?: boolean;
  }): Promise<PlanoAcaoResultadoConsulta[]> {
    try {
      const params = { ...filtros };
      const response = await this.api.get(`api/${this.route}/buscar`, { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar planos de ação:', error);
      throw error;
    }
  }

  async arquivarPlano(guid: string): Promise<void> {
    try {
      await this.api.patch(`api/${this.route}/${guid}/arquivar`);
    } catch (error) {
      console.error('Erro ao arquivar plano de ação:', error);
      throw error;
    }
  }

  async buscarAcoesPorArea(): Promise<AcaoPorArea[]> {
    try {
      const response = await this.api.get(`api/${this.route}/estatisticas/por-area`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de ações por área:', error);
      throw error;
    }
  }

  /**
   * Atualiza o status de um plano de ação
   * @param guid GUID do plano de ação
   * @param status Novo status do plano
   * @returns Promise com o plano atualizado
   */
  async atualizarStatus(guid: string, status: string): Promise<PlanoAcao> {
    try {
      // Busca o plano atual
      const planoAtual = await this.buscarPorId(guid);
      
      // Atualiza apenas o status
      const planoAtualizado = {
        ...planoAtual,
        status: status
      };
      
      // Usa o método atualizar padrão
      return await this.atualizar(planoAtualizado);
    } catch (error) {
      console.error('Erro ao atualizar status do plano de ação:', error);
      throw error;
    }
  }

  /**
   * Sobrescreve o método atualizar para verificar se o plano foi finalizado ou voltou a pendente
   * e atualizar automaticamente todas as suas tarefas
   */
  async atualizar(data: PlanoAcao): Promise<PlanoAcao> {
    // Buscar o plano atual para comparar o status
    let statusAnterior: string | undefined;
    if (data.guid) {
      try {
        const planoAtual = await this.buscarPorId(data.guid);
        statusAnterior = planoAtual.status;
      } catch (error) {
        // Se não conseguir buscar, continua com a atualização
        console.warn('Não foi possível buscar o plano anterior:', error);
      }
    }

    // Atualizar o plano
    const planoAtualizado = await super.atualizar(data);

    // Se o status mudou para "Finalizada", finalizar todas as tarefas
    if (planoAtualizado.status === 'Finalizada' && statusAnterior !== 'Finalizada') {
      // Importar dinamicamente para evitar dependência circular
      const { desdobramentoService } = await import('./DesdobramentoService');
      await desdobramentoService.finalizarTodasTarefasDoPlano(planoAtualizado.guid);
    }
    
    // Se o status mudou para "Pendente", marcar todas as tarefas como pendente
    if (planoAtualizado.status === 'Pendente' && statusAnterior !== 'Pendente') {
      // Importar dinamicamente para evitar dependência circular
      const { desdobramentoService } = await import('./DesdobramentoService');
      await desdobramentoService.marcarTodasTarefasComoPendente(planoAtualizado.guid);
    }

    return planoAtualizado;
  }
}

export const planoAcaoService = new PlanoAcaoService();